import { CSSObject } from '@emotion/react';
import {
    HoneycombTheme,
    ResponsiveStyleValue,
    createResponsiveStyles,
    useHoneycombTheme,
    ChipSize,
    ChipColor,
    ChipVariant,
    ChipElementColors,
    getSizeValue,
    ChipCornerStyle,
    HoneycombShadowSize,
} from '../../theming';

import { alpha, darken, isChipFixedColor, isChipThemeableColor } from '../../utils';

const getChipBorderRadius = (cornerStyle: ChipCornerStyle, height: number, radius: number): string | null => {
    switch (cornerStyle) {
        case 'pill':
            return `${height / 2}px`;
        case 'rounded':
            return `${radius}px`;
        default:
            return null;
    }
};

const getWidthStyles = (fullWidth: boolean): CSSObject => ({
    display: fullWidth ? 'flex' : 'inline-flex',
    width: fullWidth ? '100%' : 'auto',
});

function getSizeStyles(cornerStyle: ChipCornerStyle, theme: HoneycombTheme) {
    return (size: ChipSize) => {
        const componentTheme = theme.components.themes.Chip;
        const sizeProps = componentTheme.size[size];
        const borderRadius = getChipBorderRadius(cornerStyle, sizeProps.height, sizeProps.radius);

        return {
            fontSize: theme.typography.variant.body[sizeProps.fontSize].l.fontSize,
            height: `${sizeProps.height}px`,
            padding: `0 ${sizeProps.paddingX}px`,
            borderRadius: borderRadius || undefined,
            gap: `${sizeProps.iconClearance}px`,
            '> [data-icon="true"]': {
                width: `${sizeProps.iconSize}px`,
                height: `${sizeProps.iconSize}px`,
            },
            '&[data-clickable]': theme.fn.hoverStyles({
                boxShadow: `inset 0 ${sizeProps.height}px 0 0 var(--hover-color)`,
            }),
        };
    };
}

const getElementColors = (color: ChipColor, theme: HoneycombTheme): ChipElementColors => {
    const { palette, components } = theme;
    const componentTheme = components.themes.Chip;

    if (isChipThemeableColor(color)) {
        return componentTheme.colors[color];
    }

    if (isChipFixedColor(color)) {
        return componentTheme.fixedColors[color];
    }

    // This is a `system` color
    const systemColor = palette.system[color];
    return {
        backgroundFilled: systemColor.tint[200],
        border: systemColor.light,
        textFilled: palette.common.black,
        textOutlined: palette.common.black,
    };
};

const getColorStyles = (variant: ChipVariant, colors: ChipElementColors, theme: HoneycombTheme): CSSObject => {
    const { backgroundFilled, backgroundOutlined, border, textFilled, textOutlined } =
        theme.fn.resolveFlatColorObject(colors);

    if (variant === 'outlined') {
        return {
            color: textOutlined,
            '> [data-surface]': {
                borderColor: border,
                backgroundColor: backgroundOutlined || 'transparent',
            },
            '&[data-clickable] > [data-surface]': theme.fn.hoverStyles({
                backgroundColor: alpha(backgroundFilled, 0.05),
            }),
        };
    }
    return {
        color: textFilled,
        '> [data-surface]': {
            backgroundColor: backgroundFilled,
            borderColor: 'transparent',
        },
        '&[data-clickable] > [data-surface]': theme.fn.hoverStyles({ backgroundColor: darken(backgroundFilled, 0.1) }),
    };
};

export interface ChipStylesParams {
    size: ResponsiveStyleValue<ChipSize>;
    color: ChipColor | ChipElementColors;
    variant: ChipVariant;
    cornerStyle: ChipCornerStyle;
    fullWidth: boolean;
    shadow?: HoneycombShadowSize;
}

type ChipStyleClasses = 'root' | 'surface' | 'icon' | 'dismissIcon';

export function useStyles({ size, color, variant, cornerStyle, fullWidth, shadow }: ChipStylesParams): {
    styles: { [key in ChipStyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { typography, components, shadows, transitions } = theme;

    const fontFamily = typography.family.body;
    const componentTheme = components.themes.Chip;

    const dismissIconSize = getSizeValue(componentTheme.dismissIconSize);

    const root: CSSObject = {
        position: 'relative',
        ...getWidthStyles(fullWidth),
        '&:focus-visible > [data-surface]': {
            ...theme.focusRingStyles.styles(theme),
        },
        ...getColorStyles(variant, typeof color === 'object' ? color : getElementColors(color, theme), theme),
    };

    const surface: CSSObject = {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        ...createResponsiveStyles(
            [
                {
                    value: size,
                    getStyles: getSizeStyles(cornerStyle, theme),
                },
            ],
            theme
        ),
        whiteSpace: 'nowrap',
        lineHeight: 1,
        fontWeight: `${fontFamily.fontWeight[componentTheme.fontWeight]}`,

        borderStyle: 'solid',
        borderWidth: `${componentTheme.borderWidth}px`,

        boxShadow: shadow ? shadows[shadow] : undefined,
        transition: `all ${transitions.duration.short}ms ${transitions.easing.easeInOut}`,
    };

    const icon: CSSObject = {
        display: 'inline-block',
        fill: 'currentColor',
    };

    const dismissIcon: CSSObject = {
        zIndex: 1,
        width: `${componentTheme.dismissIconSize}px`,
        height: `${dismissIconSize}px`,
    };

    return {
        styles: {
            root,
            surface,
            icon,
            dismissIcon,
        },
    };
}
